import type {
  ShipmentCellValue,
  ShipmentHeader,
  ShipmentRow,
} from '../../types';
import {
  COLUMN_WIDTHS,
  DEFAULT_COLUMN_WIDTH,
  NOTIFICATION_ENABLED,
  NOTIFICATION_MUTED,
  PLACEHOLDER_ROW_KEY,
  SKELETON_HEADERS,
} from './shipmentTableConstants';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { formatUserDateTime } from '@/common/utils/userDateFormat';
import { parseShipmentDayjs } from '@/common/utils/utils';

dayjs.extend(utc);
dayjs.extend(timezone);

type TableTeam = {
  name?: string;
  color?: string | null;
};

type ShipmentHeaderLike = Partial<ShipmentHeader> & {
  group?: string;
  isCustom?: boolean;
  is_custom?: boolean;
  required?: boolean;
};

const CORE_COLUMN_LABEL_BY_KEY = new Map(
  SKELETON_HEADERS.map((header) => [header.key, header.value])
);
const CORE_COLUMN_KEYS = new Set(SKELETON_HEADERS.map((header) => header.key));
const EXCLUDED_HEADER_KEYS = new Set(['id', 'alert_type']);
const CUSTOMER_EXECUTION_ROW_TYPE_KEY = '__customerExecutionRowType';
const CUSTOMER_EXECUTION_PARENT_ROW_TYPE = 'parent';
const CUSTOMER_EXECUTION_CHILD_ROW_TYPE = 'child';
const CUSTOMER_EXECUTION_LOADING_ROW_TYPE = 'loading';

export const markCustomerExecutionParentRow = (
  record: ShipmentRow
): ShipmentRow => ({
  ...record,
  [CUSTOMER_EXECUTION_ROW_TYPE_KEY]: CUSTOMER_EXECUTION_PARENT_ROW_TYPE,
});

export const markCustomerExecutionChildRow = (
  record: ShipmentRow
): ShipmentRow => ({
  ...record,
  [CUSTOMER_EXECUTION_ROW_TYPE_KEY]: CUSTOMER_EXECUTION_CHILD_ROW_TYPE,
});

export const markCustomerExecutionLoadingRow = (
  record: ShipmentRow
): ShipmentRow => ({
  ...record,
  [CUSTOMER_EXECUTION_ROW_TYPE_KEY]: CUSTOMER_EXECUTION_LOADING_ROW_TYPE,
});

export const isCustomerExecutionParentRow = (record: ShipmentRow): boolean =>
  record[CUSTOMER_EXECUTION_ROW_TYPE_KEY] ===
  CUSTOMER_EXECUTION_PARENT_ROW_TYPE;

export const isCustomerExecutionChildRow = (record: ShipmentRow): boolean =>
  record[CUSTOMER_EXECUTION_ROW_TYPE_KEY] === CUSTOMER_EXECUTION_CHILD_ROW_TYPE;

export const isCustomerExecutionLoadingRow = (record: ShipmentRow): boolean =>
  record[CUSTOMER_EXECUTION_ROW_TYPE_KEY] ===
  CUSTOMER_EXECUTION_LOADING_ROW_TYPE;

export const normalizeShipmentHeaders = (
  headers?: ShipmentHeaderLike[],
  previousHeaders: ShipmentHeader[] = []
): ShipmentHeader[] => {
  const list = Array.isArray(headers) ? headers : [];
  const previousLabelByKey = new Map(
    previousHeaders.map((header) => [header.key, header.value])
  );
  const nextHeaders = list
    .filter(
      (header) =>
        header.key && header.value && !EXCLUDED_HEADER_KEYS.has(header.key)
    )
    .map((header) => {
      const key = String(header.key);

      return {
        ...header,
        key,
        value:
          CORE_COLUMN_LABEL_BY_KEY.get(key) ??
          previousLabelByKey.get(key) ??
          String(header.value),
      };
    });
  const headerByKey = new Map(
    nextHeaders.map((header) => [header.key, header])
  );
  const orderedCoreHeaders = SKELETON_HEADERS.map((header) => ({
    ...header,
    ...headerByKey.get(header.key),
    value: CORE_COLUMN_LABEL_BY_KEY.get(header.key) ?? header.value,
  }));
  const optionalHeaders = nextHeaders.filter(
    (header) => !CORE_COLUMN_KEYS.has(header.key)
  );

  return [...orderedCoreHeaders, ...optionalHeaders];
};

export const getColumnWidth = (key: string): number =>
  COLUMN_WIDTHS[key] || DEFAULT_COLUMN_WIDTH;

export const isPlaceholderRow = (record: ShipmentRow): boolean =>
  Boolean(record[PLACEHOLDER_ROW_KEY]);

type CustomerReferenceSource = {
  reference_number?: unknown;
  link_code?: unknown;
  shipment_id?: unknown;
  id?: unknown;
};

const toNonEmptyText = (value: unknown): string | null => {
  const normalized = String(value ?? '').trim();
  return normalized.length > 0 ? normalized : null;
};

export const getCustomerReference = (
  source: CustomerReferenceSource
): string => {
  return (
    toNonEmptyText(source.reference_number) ||
    toNonEmptyText(source.link_code) ||
    toNonEmptyText(source.shipment_id) ||
    toNonEmptyText(source.id) ||
    'Unknown'
  );
};

export const getShipmentRowId = (record: ShipmentRow): string =>
  String(
    record[PLACEHOLDER_ROW_KEY] ||
      record.id ||
      record.shipment_id ||
      record.reference_number ||
      record.tracking_num ||
      record.link_code ||
      ''
  );

export const isEmptyValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

export const formatValue = (value: ShipmentCellValue): string => {
  if (isEmptyValue(value)) return '';

  if (Array.isArray(value)) {
    return value
      .filter((item) => !isEmptyValue(item))
      .map(String)
      .join(', ');
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
};

export const formatCarrierName = (value?: unknown): string => {
  const carrierName = textOrEmpty(value);
  return carrierName.trim().toLowerCase() === 'other' ? 'Unknown' : carrierName;
};

export const getNotificationState = (record: ShipmentRow): string => {
  const value = record.has_notification ?? record.hasNotification;

  if (typeof value === 'string') {
    const normalizedValue = value.toLowerCase();

    if (
      normalizedValue === NOTIFICATION_ENABLED ||
      normalizedValue === NOTIFICATION_MUTED
    ) {
      return normalizedValue;
    }
  }

  if (value === true) return NOTIFICATION_ENABLED;

  return 'disabled';
};

export const getTeams = (record: ShipmentRow): TableTeam[] =>
  Array.isArray(record.teams) ? (record.teams as TableTeam[]) : [];

export const textOrEmpty = (value?: unknown): string => {
  if (isEmptyValue(value)) return '';
  return String(value);
};

const getTimezoneShortName = (timeZone?: unknown): string => {
  if (typeof timeZone !== 'string' || !timeZone) return '';

  try {
    return (
      new Date()
        .toLocaleTimeString('en-US', {
          timeZone,
          timeZoneName: 'short',
        })
        .split(' ')
        .pop() ?? ''
    );
  } catch {
    return '';
  }
};

/**
 * Best-effort parse of a "Y-M-D"-shaped date string whose field order the
 * backend has been observed sending inconsistently (e.g. "17-12-2026",
 * "2026-17-12", and "12-17-2026" have all been seen for the same Dec 17,
 * 2026 date). Tries candidate orderings (ISO first) and accepts the first
 * one where every field is in a plausible range — dayjs's own parser can't
 * be trusted here: `dayjs.tz(str, format, tz)` throws outright when a format
 * doesn't strict-match instead of returning an invalid date, and even a
 * matching format silently overflows out-of-range values (e.g. treats month
 * 17 as month 5 of the following year) instead of rejecting them.
 */
const parseAmbiguousShipmentDate = (
  rawValue: string,
  dateFormat?: string | null
): {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
} | null => {
  const match = rawValue.match(
    /^(\d{1,4})-(\d{1,2})-(\d{1,4})(?:[T ](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?/
  );
  if (!match) return null;

  const [, a, b, c, h, mi, s] = match;
  const hour = h ? Number(h) : 0;
  const minute = mi ? Number(mi) : 0;
  const second = s ? Number(s) : 0;
  if (hour > 23 || minute > 59 || second > 59) return null;

  const numA = Number(a);
  const numB = Number(b);
  const numC = Number(c);
  const isYear = (n: number) => n >= 1000 && n <= 9999;
  const isMonth = (n: number) => n >= 1 && n <= 12;
  const isDay = (n: number) => n >= 1 && n <= 31;

  // ISO year-first first, then the caller's profile order, then the other
  // day/month swap, then YYYY-DD-MM as a last resort for out-of-range months.
  const ymd = { year: numA, month: numB, day: numC };
  const mdy = { year: numC, month: numA, day: numB };
  const dmy = { year: numC, month: numB, day: numA };
  const ydm = { year: numA, month: numC, day: numB };
  const candidates =
    dateFormat === 'DD/MM/YYYY' ? [ymd, dmy, mdy, ydm] : [ymd, mdy, dmy, ydm];

  for (const candidate of candidates) {
    if (
      isYear(candidate.year) &&
      isMonth(candidate.month) &&
      isDay(candidate.day)
    ) {
      return { ...candidate, hour, minute, second };
    }
  }
  return null;
};

export const formatEstimatedArrival = (
  dateValue?: unknown,
  timeZone?: unknown,
  status?: string,
  dateFormat?: string | null
): string => {
  if (dateValue === null || dateValue === undefined) return '';

  const rawValue = String(dateValue).trim();
  if (!rawValue) return '';

  const tz =
    typeof timeZone === 'string' && timeZone ? timeZone : dayjs.tz.guess();

  const parsedDayjs = parseShipmentDayjs(rawValue, dateFormat);
  const parsed = parsedDayjs
    ? {
        year: parsedDayjs.year(),
        month: parsedDayjs.month() + 1,
        day: parsedDayjs.date(),
        hour: parsedDayjs.hour(),
        minute: parsedDayjs.minute(),
        second: parsedDayjs.second(),
      }
    : parseAmbiguousShipmentDate(rawValue, dateFormat);
  if (!parsed) return rawValue;

  const pad = (n: number) => String(n).padStart(2, '0');
  const canonicalIso = `${parsed.year}-${pad(parsed.month)}-${pad(parsed.day)}T${pad(parsed.hour)}:${pad(parsed.minute)}:${pad(parsed.second)}`;

  let date: dayjs.Dayjs;
  try {
    date = dayjs.tz(canonicalIso, tz);
  } catch {
    return rawValue;
  }
  if (!date.isValid()) return rawValue;

  const now = dayjs().tz(tz);

  const diffHours = date.diff(now, 'hour', true);
  const prefix = status === 'DELIVERED' ? '' : 'ETA';

  // ---------- 48-hour rule with prefix + time ----------
  if (Math.abs(diffHours) <= 48) {
    const relative = date.isSame(now, 'day')
      ? 'Today'
      : date.isSame(now.add(1, 'day'), 'day')
        ? 'Tomorrow'
        : date.isSame(now.subtract(1, 'day'), 'day')
          ? 'Yesterday'
          : null;

    if (relative) {
      const time = date.format('hA').replace(':00', '');
      const tzShort = getTimezoneShortName(tz);
      return `${prefix} ${relative} ${time}${tzShort ? ' ' + tzShort : ''}`;
    }
  }

  // ---------- absolute fallback ----------
  const absoluteDate = formatUserDateTime(date, dateFormat, {
    includeYear: false,
    includeTime: false,
  });
  const time = date.format('h:mm A').replace(':00', '');
  const tzShort = getTimezoneShortName(tz);

  return tzShort
    ? `${prefix} ${absoluteDate}, ${time} ${tzShort}`
    : `${prefix} ${absoluteDate}, ${time}`;
};

export const getLocationName = (
  location: unknown,
  fallback: string
): string => {
  const name =
    location && typeof location === 'object' && 'name' in location
      ? String(location.name || '')
      : '';
  if (!name || name === 'Unknown Location') return fallback;
  return name;
};
